import React, { useCallback, useRef } from "react";
import ReactDOM from "react-dom/client";
import { AuthProvider, SignInCard, useAuth } from "@jurisui/auth-provider";
import { createClient, type SupabaseClient } from "@supabase/supabase-js";

import "@jurisui/core/styles.css";
import App from "./App";
import "./styles.css";

const testMode = import.meta.env.VITE_LIVE_DOCS_TEST_MODE === "true";
const identityUrl = String(import.meta.env.VITE_IDENTITY_SUPABASE_URL ?? "");
const identityKey = String(
  import.meta.env.VITE_IDENTITY_SUPABASE_ANON_KEY ??
    import.meta.env.VITE_IDENTITY_SUPABASE_PUBLISHABLE_KEY ??
    "",
);
const identityClient = !testMode && identityUrl && identityKey
  ? createClient(identityUrl, identityKey, {
      auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
    })
  : null;

function AuthenticatedLiveDocs({ supabase }: { supabase: SupabaseClient }) {
  const { user, session, loading, signOut } = useAuth();
  const accessToken = useRef(session?.access_token);
  accessToken.current = session?.access_token;
  const getAccessToken = useCallback(async () => accessToken.current ?? null, []);
  if (loading) return <main className="auth-gate">Checking your ICC session…</main>;
  if (!user) {
    return (
      <SignInCard
        supabase={supabase}
        appName="Live Docs"
        methods={{ email: true, password: false, azure: false }}
      />
    );
  }
  return <App key={user.id} getAccessToken={getAccessToken} account={{ name: String(user.user_metadata?.full_name ?? user.email ?? "ICC account"), email: user.email, onSignOut: signOut }} />;
}

function Root() {
  if (testMode) return <App getAccessToken={async () => "deterministic-test-token"} />;
  if (!identityClient) {
    return <main className="auth-gate" role="alert">The Juris identity configuration is missing.</main>;
  }
  return (
    <AuthProvider supabase={identityClient} enforceIccEligibility>
      <AuthenticatedLiveDocs supabase={identityClient} />
    </AuthProvider>
  );
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <Root />
  </React.StrictMode>,
);
